var _excluded = ["width"],
    _excluded2 = ["rootProps"],
    _excluded3 = ["children"];

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

/* eslint-disable max-len */
import { chakra, forwardRef, omitThemingProps, StylesProvider, useMultiStyleConfig, useStyles } from '@chakra-ui/system';
import { __DEV__, cx } from '@chakra-ui/utils';
import { motion } from 'framer-motion';
import * as React from 'react';
import { CaretDownIcon, CaretUpIcon } from './icons';
import { TimePickerDescendantsProvider, TimePickerProvider, useTimePicker, useTimePickerContext, useTimePickerInput, useTimePickerInputGroup, useTimePickerItem, useTimePickerList, useTimePickerPositioner } from './use-time-picker';

/**
 * TimePicker provides context, state, and focus management
 * to its sub-components. It doesn't render any DOM node.
 */
export var TimePicker = forwardRef((props, ref) => {
  var styles = useMultiStyleConfig('TimePicker', props);
  var ownProps = omitThemingProps(props);
  var ctx = useTimePicker(ownProps);
  var context = React.useMemo(() => ctx, [ctx]);
  var {
    descendants
  } = context;
  return /*#__PURE__*/React.createElement(TimePickerDescendantsProvider, {
    value: descendants
  }, /*#__PURE__*/React.createElement(TimePickerProvider, {
    value: context
  }, /*#__PURE__*/React.createElement(StylesProvider, {
    value: styles
  }, /*#__PURE__*/React.createElement(chakra.div, {
    ref: ref
  }, /*#__PURE__*/React.createElement(TimePickerInputGroup, null), /*#__PURE__*/React.createElement(TimePickerList, null)))));
});

if (__DEV__) {
  TimePicker.displayName = 'TimePicker';
}

/**
 * The trigger for the time picker list. Must be a direct child of `TimePicker`.
 */
export var TimePickerInputGroup = forwardRef((props, ref) => {
  var {
    width = '100%'
  } = props,
      rest = _objectWithoutPropertiesLoose(props, _excluded);

  var inputGroupCtx = useTimePickerInputGroup(rest, ref);
  var styles = useStyles();
  var CaretIcon = inputGroupCtx['aria-expanded'] ? CaretUpIcon : CaretDownIcon;

  var inputGroupStyles = _extends({
    width
  }, styles.inputGroup);

  return /*#__PURE__*/React.createElement(chakra.div, _extends({}, inputGroupCtx, {
    __css: inputGroupStyles,
    className: cx('time-picker__input-group', props.className)
  }), /*#__PURE__*/React.createElement(TimePickerInput, null), /*#__PURE__*/React.createElement(CaretIcon, {
    color: "brand.500",
    display: {
      base: 'none',
      md: 'block'
    }
  }));
});
/**
 * The trigger for the time picker list. Must be a direct child of `TimePicker`.
 */

export var TimePickerInput = forwardRef((props, ref) => {
  var inputCtx = useTimePickerInput(props, ref);
  var {
    value,
    placeholder
  } = inputCtx;
  var styles = useStyles();
  var inputValue = value === '' ? placeholder : value;

  var inputStyles = _extends({
    pointerEvents: 'none'
  }, styles.input);

  return /*#__PURE__*/React.createElement(chakra.button, _extends({}, inputCtx, {
    __css: inputStyles,
    className: cx('time-picker__input', props.className)
  }), inputValue);
});

if (__DEV__) {
  TimePickerInputGroup.displayName = 'TimePickerInput';
}

var motionVariants = {
  enter: {
    visibility: 'visible',
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: {
    transitionEnd: {
      visibility: 'hidden'
    },
    opacity: 0,
    scale: 0.8,
    transition: {
      duration: 0.1,
      easings: 'easeOut'
    }
  }
};
var Motion = chakra(motion.div);
export var TimePickerList = forwardRef((props, ref) => {
  var _props$zIndex, _styles$list;

  var {
    rootProps
  } = props,
      rest = _objectWithoutPropertiesLoose(props, _excluded2);

  var {
    isOpen,
    onTransitionEnd,
    ListId
  } = useTimePickerContext();
  var ctx = useTimePickerList(rest, ref);
  var positionerProps = useTimePickerPositioner(rootProps);
  var styles = useStyles();
  var hasUnavailable = ctx.unavailable && ctx.unavailable.length > 0;
  return /*#__PURE__*/React.createElement(chakra.div, _extends({}, positionerProps, {
    __css: {
      zIndex: (_props$zIndex = props.zIndex) != null ? _props$zIndex : (_styles$list = styles.list) == null ? void 0 : _styles$list.zIndex
    }
  }), /*#__PURE__*/React.createElement(Motion, _extends({}, ctx, {
    /**
     * We could call this on either `onAnimationComplete` or `onUpdate`.
     * It seems the re-focusing works better with the `onUpdate`
     */
    __css: _extends({
      outline: 0
    }, styles.list),
    animate: isOpen ? 'enter' : 'exit',
    className: cx('time-picker__list', ctx.className),
    initial: false,
    onUpdate: onTransitionEnd,
    variants: motionVariants
  }), /*#__PURE__*/React.createElement(chakra.div, {
    __css: styles.scroll
  }, ctx.hoursList.map(time => {
    var disabled = hasUnavailable && ctx.unavailable.indexOf(time.value) > -1;
    return /*#__PURE__*/React.createElement(TimePickerItem, {
      isDisabled: disabled,
      key: ListId + "-" + time.value,
      onClick: () => ctx.setSelectedHour(time.value)
    }, time.label);
  }))));
});

if (__DEV__) {
  TimePickerList.displayName = 'TimePickerList';
}

export var TimePickerItem = forwardRef((props, ref) => {
  var {
    children
  } = props,
      rest = _objectWithoutPropertiesLoose(props, _excluded3);

  var styles = useStyles();
  var ctx = useTimePickerItem(rest, ref);
  return /*#__PURE__*/React.createElement(chakra.button, _extends({
    __css: styles.item,
    className: cx('time-picker__item', ctx.className),
    type: "button"
  }, ctx), children);
});

if (__DEV__) {
  TimePickerItem.displayName = 'TimePickerItem';
}
//# sourceMappingURL=time-picker.js.map